/**
 * Centralized debounced Firestore update utilities
 * 
 * Following user rule: Wait 2 seconds after user stops editing any field 
 * before updating the Firestore database to ensure smooth user experience.
 * 
 * This module provides consistent debouncing patterns across the entire webapp
 * to prevent interruptions while users are typing and ensure responsive UI.
 */

import { debounceManager } from '../repositories/firestoreUtils';

export const DEBOUNCE_DELAY_MS = 2000; // 2 seconds as specified by user

/**
 * Standard debounced field update pattern
 * Updates local state immediately, debounces Firestore update
 */
export function createDebouncedFieldUpdater<T extends Record<string, any>>(
  setLocalState: (updater: (prev: T) => T) => void,
  firestoreUpdate: (field: string, value: string) => Promise<void>,
  options: {
    trackChange?: (details: string) => void;
    updateTimestamp?: (field: string) => void;
    keyPrefix?: string;
  } = {}
) {
  const { trackChange, updateTimestamp, keyPrefix = 'field' } = options;

  return (field: string, value: string) => {
    // 1. Update local state immediately for responsive UI
    setLocalState(prev => ({ ...prev, [field]: value }));
    
    // 2. Update timestamp if provided
    if (updateTimestamp) {
      updateTimestamp(field);
    }
    
    // 3. Track change immediately for version control
    if (trackChange) {
      trackChange(`Updated ${field}`);
    }
    
    // 4. Debounced Firestore update - wait 2 seconds after user stops typing
    const debounceKey = `${keyPrefix}:${field}`;
    debounceManager.schedule(
      debounceKey,
      async () => {
        try {
          await firestoreUpdate(field, value);
        } catch (error) {
          console.error(`Failed to update ${field}:`, error);
        }
      },
      DEBOUNCE_DELAY_MS
    );
  };
}

/**
 * Debounced array item update pattern
 * For updating items within arrays/collections with field-level debouncing
 */
export function createDebouncedArrayUpdater<T extends { id: string }>(
  setLocalState: (updater: (prev: T[]) => T[]) => void,
  firestoreUpdate: (id: string, field: string, value: string) => Promise<void>,
  options: {
    trackChange?: (id: string, field: string, details: string) => void;
    updateTimestamp?: (id: string, field: string) => void;
    keyPrefix?: string;
  } = {}
) {
  const { trackChange, updateTimestamp, keyPrefix = 'array' } = options;

  return (id: string, field: string, value: string) => {
    // 1. Update local state immediately
    setLocalState(prev => 
      prev.map(item => 
        item.id === id ? { ...item, [field]: value } : item
      )
    );
    
    // 2. Update timestamp if provided
    if (updateTimestamp) {
      updateTimestamp(id, field);
    }
    
    // 3. Track change immediately
    if (trackChange) {
      trackChange(id, field, `Updated ${field} for item ${id}`);
    }
    
    // 4. Debounced Firestore update per field per item
    const debounceKey = `${keyPrefix}:${id}:${field}`;
    debounceManager.schedule(
      debounceKey,
      async () => {
        try {
          await firestoreUpdate(id, field, value);
        } catch (error) {
          console.error(`Failed to update ${field} for item ${id}:`, error);
        }
      },
      DEBOUNCE_DELAY_MS
    );
  };
}

/**
 * Flush all pending updates
 * Use this when navigating away or when immediate save is required
 */
export function flushAllPendingUpdates(): void {
  try {
    debounceManager.flushAll();
  } catch {}
}

/**
 * Check if there are pending updates for a specific key
 */
export function hasPendingUpdates(key?: string): boolean {
  return debounceManager.hasPending(key);
}

/**
 * Get all pending update keys
 */
export function getPendingUpdateKeys(): string[] {
  return debounceManager.getPendingKeys();
}

/**
 * Cancel pending updates for a specific key
 */
export function cancelPendingUpdate(key: string): void {
  debounceManager.cancel(key);
}

/**
 * Higher-order function that wraps any function with debouncing
 * Useful for custom update patterns
 */
export function withDebounce<T extends (...args: any[]) => Promise<void> | void>(
  fn: T,
  key: string,
  delay: number = DEBOUNCE_DELAY_MS
): (...args: Parameters<T>) => void {
  return (...args: Parameters<T>) => {
    debounceManager.schedule(
      key,
      async () => {
        try {
          await fn(...args);
        } catch (error) {
          console.error(`Debounced function failed for key ${key}:`, error);
        }
      },
      delay
    );
  };
}

/**
 * Example pattern for React hook for debounced form field updates
 * To use this pattern in a component:
 * 
 * import { useState, useCallback } from 'react';
 * 
 * const [localValue, setLocalValue] = useState(initialValue);
 * const [hasChanges, setHasChanges] = useState(false);
 * 
 * const updateField = useCallback((value) => {
 *   setLocalValue(value);
 *   setHasChanges(true);
 *   
 *   debounceManager.schedule(
 *     key,
 *     async () => {
 *       try {
 *         await firestoreUpdate(value);
 *         setHasChanges(false);
 *       } catch (error) {
 *         console.error(`Failed to update field ${key}:`, error);
 *       }
 *     },
 *     DEBOUNCE_DELAY_MS
 *   );
 * }, [firestoreUpdate, key]);
 */

// Export the debounce delay constant for consistency
export { DEBOUNCE_DELAY_MS as DEFAULT_DEBOUNCE_DELAY };
